import React from 'react';
import { cn } from '@express-labs/raven-tools';
import ColorSwatch from '../../../../ColorSwatch/ColorSwatch';
import Link from '../../../../Link/Link';
import NavLink from '../../../../NavLink/NavLink';
import s from './ProductColors.scss';

export interface ProductColorProps {
  skuUpc: string;
  color: string;
}

type ColorSwatchesProp = ProductColorProps[] | string[];

export interface ProductColorSwatchProps {
  additionalColorsLink?: string;
  className?: string;
  colorSwatches?: ColorSwatchesProp;
  colorCount?: number;
  displayColorSwatches?: boolean;
  maximumNumberOfSwatches?: number;
  onBlurColor?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  onChangeColor?: (
    ev: React.ChangeEvent<HTMLInputElement>,
    productId?: string
  ) => void;
  onFocusColor?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  onMouseEnterColor?: (ev: React.MouseEvent<HTMLInputElement>) => void;
  onMouseLeaveColor?: (ev: React.MouseEvent<HTMLInputElement>) => void;
  selectedSkuUpc?: string;
  productId?: string;
  hideColorSwatch?: boolean;
}

type ColorsDistProps = {
  colorSwatches: ColorSwatchesProp;
  maxSwatches: number;
};

type ColorsDistributionResult = {
  colorsToDisplay: ColorSwatchesProp;
  hiddenColors: ColorSwatchesProp;
};

type AdditionalColorsProps = {
  additionalColorsLink?: string;
  children: React.ReactNode;
};

export const getColorsDistribution = ({
  colorSwatches,
  maxSwatches,
}: ColorsDistProps): ColorsDistributionResult => {
  const numberOfColors = colorSwatches.length;

  if (numberOfColors > 0) {
    const colorsToDisplay = colorSwatches.slice(0, maxSwatches);
    const hiddenColors = colorSwatches.slice(maxSwatches);
    return {
      colorsToDisplay,
      hiddenColors,
    };
  }
  return {
    colorsToDisplay: [],
    hiddenColors: [],
  };
};

export const AdditionalColors = ({
  additionalColorsLink,
  children,
}: AdditionalColorsProps) => {
  if (additionalColorsLink && additionalColorsLink.startsWith('http')) {
    return (
      <Link className={s.additionalSwatches} href={additionalColorsLink}>
        {children}
      </Link>
    );
  }
  if (additionalColorsLink && additionalColorsLink.startsWith('/')) {
    return (
      <NavLink className={s.additionalSwatches} to={additionalColorsLink}>
        {children}
      </NavLink>
    );
  }
  return <div className={s.additionalSwatches}>{children}</div>;
};

export const ColorPicker = ({
  colorSwatches = [],
  onBlurColor,
  onChangeColor,
  onMouseEnterColor,
  onMouseLeaveColor,
  selectedSkuUpc,
  productId,
}: ProductColorSwatchProps) => (
  <>
    {colorSwatches.map((colorSwatch) => {
      const isString = typeof colorSwatch === 'string';

      let swatchColorCode;
      let colorImage;

      if (isString) {
        swatchColorCode = colorSwatch.slice(65, 69);
        colorImage = colorSwatch;
      } else {
        swatchColorCode = colorSwatch.skuUpc;
        colorImage = `https://images.express.com/is/image/expressfashion/${swatchColorCode}_s?cache=on&wid=30&hei=30&qlt=90&defaultImage=Photo-Coming-Soon`;
      }

      return (
        <ColorSwatch
          className={s.colorSwatch}
          backgroundImage={colorImage}
          checked={swatchColorCode === selectedSkuUpc}
          colorName={swatchColorCode}
          id={swatchColorCode}
          key={swatchColorCode}
          onBlur={onBlurColor}
          onChange={onChangeColor}
          onMouseEnter={onMouseEnterColor}
          onMouseLeave={onMouseLeaveColor}
          tabIndex={swatchColorCode === selectedSkuUpc ? 0 : -1}
          value={swatchColorCode}
          productId={productId}
        />
      );
    })}
  </>
);

const ProductColors: React.FC<ProductColorSwatchProps> = ({
  additionalColorsLink,
  className,
  colorCount = 0,
  colorSwatches = [],
  displayColorSwatches = true,
  maximumNumberOfSwatches = 4,
  onBlurColor,
  onChangeColor,
  onMouseEnterColor,
  onMouseLeaveColor,
  selectedSkuUpc,
  productId,
  hideColorSwatch = false,
}) => {
  const numberOfColors = colorSwatches?.length || colorCount;
  const displaySwatches = displayColorSwatches && numberOfColors > 0;
  const displayNumbOfColors = !displayColorSwatches && numberOfColors > 0;
  const hideColors = hideColorSwatch && numberOfColors === 1;

  if (hideColors) {
    return null;
  }

  if (displaySwatches) {
    const colorDistribution = getColorsDistribution({
      colorSwatches,
      maxSwatches: maximumNumberOfSwatches,
    });
    const { colorsToDisplay, hiddenColors } = colorDistribution;
    const additionalColors = hiddenColors.length;
    const shouldDisplayMoreLink = additionalColors > 0;

    return (
      <div
        className={cn(
          s.colorSwatchesContainer,
          'product-colors--swatch-container'
        )}
      >
        <ColorPicker
          colorSwatches={colorsToDisplay}
          onBlurColor={onBlurColor}
          onChangeColor={onChangeColor}
          onMouseEnterColor={onMouseEnterColor}
          onMouseLeaveColor={onMouseLeaveColor}
          selectedSkuUpc={selectedSkuUpc}
          productId={productId}
        />
        {shouldDisplayMoreLink && (
          <AdditionalColors additionalColorsLink={additionalColorsLink}>
            <span className={s.additionalSwatchesText}>
              {`+${additionalColors}`}
            </span>
          </AdditionalColors>
        )}
      </div>
    );
  }

  if (displayNumbOfColors) {
    return (
      <p className={cn(s.numberOfColors, className)}>
        <span
          className={s.totalNumberOfColors}
        >{`${numberOfColors} color${numberOfColors > 1 ? 's' : ''}`}</span>
      </p>
    );
  }

  return null;
};

export default ProductColors;
